import React from 'react';
import { InfoIcon, SparklesIcon } from 'lucide-react';
import { PractitionerFlowShell } from '../components/practitioners/PractitionerFlowShell';
import { AccountForm } from '../components/practitioners/AccountForm';
import { MembershipPlans } from '../components/practitioners/MembershipPlans';
import { ResumeSignIn } from '../components/practitioners/ResumeSignIn';
import { usePractitionerSignup } from '../hooks/usePractitionerSignup';
import { foundingMembershipMessage, foundingPlans } from '../data/foundingOffer';
import type { SignupStep } from '../types/practitionerSignup';
interface PractitionerJoinPageProps {
  /** '/practitioners/join' → 'account', '/practitioners/membership' → 'membership' */
  initialStep: SignupStep;
}
export function PractitionerJoinPage({
  initialStep
}: PractitionerJoinPageProps) {
  const flow = usePractitionerSignup(initialStep);
  const selectedPlanName = foundingPlans.find(p => p.id === flow.selectedPlan)?.name ?? null;
  if (flow.step === 'account') {
    const emailExists = flow.accountError?.code === 'email_exists';
    return <PractitionerFlowShell currentStep={0} data-id="element-653" data-mp="2i51om81v7atj">
        <div className="max-w-xl mx-auto px-6" data-id="element-654" data-mp="2i49rutwj771o">
          <h1 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.1] tracking-tight text-center" data-id="element-655" data-mp="2d5yi0j68kwjg">
            Create your practitioner account
          </h1>
          <p className="mt-5 text-lg text-charcoal/70 font-light leading-relaxed text-center" data-id="element-656" data-mp="284smkdl6yv68">
            Join Fem Entity and become part of Canada's national women's health network. Create your account, choose
            your membership, then complete your practitioner profile and verification in the Fem Entity app.
          </p>

          <div className="mt-10 rounded-3xl border border-purple-200 bg-white px-6 py-8 md:px-9 md:py-9 shadow-[0_18px_50px_-30px_rgba(44,44,44,0.5)]" data-id="element-657" data-mp="2rv4ngovo3izs">
            <AccountForm submitting={flow.accountSubmitting} serverError={flow.accountError?.message ?? null} serverErrorAction={emailExists ? <button type="button" onClick={() => flow.goTo('membership')} className="font-semibold text-red-900 underline underline-offset-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600" data-id="element-659" data-mp="1hus83uzqds8l">
                    Sign in to choose your membership
                  </button> : null} onSubmit={flow.submitAccount} data-id="element-658" data-mp="1kim6d41m694x" />
          </div>

          <p className="mt-6 text-center text-sm text-charcoal/65 leading-relaxed" data-id="element-660" data-mp="13z6mzywmbslg">
            Already have a Fem Entity practitioner account? Sign in through the{' '}
            <a href="/get-the-app" className="font-medium text-purple-700 underline underline-offset-2 decoration-purple-300 hover:text-purple-900 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600" data-id="element-661" data-mp="u824wqbnbrme">
              Fem Entity app
            </a>
            .
          </p>
        </div>
      </PractitionerFlowShell>;
  }
  return <PractitionerFlowShell currentStep={1} data-id="element-662" data-mp="3ssojriigcadt">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-663" data-mp="al8tzcvcf9wn">
        <div className="max-w-2xl mx-auto text-center" data-id="element-664" data-mp="ams8oz1cpcwb">
          <h1 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.1] tracking-tight" data-id="element-665" data-mp="5m4jmiwon64q">
            Choose your Fem Entity membership
          </h1>
          <p className="mt-6 inline-flex items-start gap-2.5 rounded-2xl bg-purple-800 px-5 py-4 text-left text-base text-white leading-relaxed" data-id="element-666" data-mp="khbukeajr0hh">
            <SparklesIcon aria-hidden="true" className="mt-1 w-4 h-4 shrink-0 text-purple-200" data-id="element-667" data-mp="tax67orf01mo" />
            {foundingMembershipMessage}
          </p>
        </div>

        <div className="max-w-3xl mx-auto mt-8 space-y-4" data-id="element-668" data-mp="13a60yju3tka3">
          {flow.checkoutCancelled && !flow.needsSignIn && <p role="status" className="flex items-start gap-2.5 rounded-xl border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-charcoal/80" data-id="element-669" data-mp="ybunlz4lorn4">
              <InfoIcon aria-hidden="true" className="mt-0.5 w-4 h-4 shrink-0 text-purple-700" data-id="element-670" data-mp="1ib3aso1gs9yw" />
              Checkout was cancelled and you haven't been charged. Choose a membership whenever you're ready.
            </p>}
          {flow.checkoutError && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-id="element-671" data-mp="1w39hzkmn36m7">
              {flow.checkoutError}
            </p>}
          {flow.needsSignIn && <ResumeSignIn planName={selectedPlanName} submitting={flow.signInSubmitting} error={flow.signInError} onSubmit={flow.resumeWithSignIn} onCancel={flow.cancelSignIn} data-id="element-672" data-mp="13gf79ffmjllx" />}
        </div>

        <div className="mt-12" data-id="element-673" data-mp="2jtdktps072c5">
          <MembershipPlans selectedPlan={flow.selectedPlan} redirectingPlan={flow.redirectingPlan} onChoose={flow.choosePlan} data-id="element-674" data-mp="2jw9kpjnew138" />
        </div>

        <p className="mt-10 max-w-2xl mx-auto text-center text-sm text-charcoal/60 leading-relaxed" data-id="element-675" data-mp="d665sc4btwzd">
          You'll be taken to Stripe's secure checkout to enter payment details — Fem Entity never sees or stores your
          card information. Taxes may apply.
        </p>
      </div>
    </PractitionerFlowShell>;
}